/**
 * Маршруты админки. Пути — относительно `root` и без ведущего слэша; путь с параметром —
 * функция: без аргумента отдаёт шаблон для роутера, с аргументом — готовый путь.
 * Модуль подключается в основной бандл (App.tsx) — держать без зависимостей.
 */
export const ADMIN_ROUTES = {
  root: '/admin',

  home: '',
  login: 'auth/login',
  /** Принятие приглашения: `?token=…` из ссылки, которую выдал суперадминистратор. */
  acceptInvite: 'auth/invite',

  clients: 'clients',
  /** Карточка клиента. */
  client: (clientId?: string): string =>
    `clients/${clientId ? encodeURIComponent(clientId) : ':clientId'}`,
  modules: 'modules',
  banners: 'banners',
  /** Новый баннер; статический сегмент выигрывает у `:bannerId`. */
  bannerCreate: 'banners/new',
  /** Карточка баннера. */
  banner: (bannerId?: string): string =>
    `banners/${bannerId ? encodeURIComponent(bannerId) : ':bannerId'}`,
  docs: 'docs',
  /** Карточка юридического документа по типу (`privacy-policy`). */
  legalDocument: (type?: string): string =>
    `docs/${type ? encodeURIComponent(type) : ':documentType'}`,
  /** Новый шаблон; статический сегмент выигрывает у `:templateKey`. */
  templateCreate: 'docs/templates/new',
  /** Карточка шаблона по ключу (`push.application-approved`). */
  template: (key?: string): string =>
    `docs/templates/${key ? encodeURIComponent(key) : ':templateKey'}`,
  localization: 'localization',
  settings: 'settings',
  admins: 'admins',
  audit: 'audit'
};

/** Абсолютный путь внутри админки: `''` → `/admin`, `'banners'` → `/admin/banners`. */
export const getFullAdminRoute = (route: string): string =>
  route ? `${ADMIN_ROUTES.root}/${route}` : ADMIN_ROUTES.root;

/** Ссылка-приглашение без origin: `/admin/auth/invite?token=…`. */
export const buildAdminInvitePath = (inviteToken: string): string =>
  `${getFullAdminRoute(ADMIN_ROUTES.acceptInvite)}?token=${encodeURIComponent(inviteToken)}`;

/**
 * Быстрые переходы с главной: query-параметр, по которому раздел сразу открывает действие
 * (`/admin/modules?action=register-service`). Раздел читает его при монтировании и убирает из URL.
 */
export const ADMIN_ACTION_PARAM = 'action';

export const ADMIN_ACTIONS = {
  registerService: 'register-service',
  inviteAdmin: 'invite-admin'
} as const;

export type TAdminAction = (typeof ADMIN_ACTIONS)[keyof typeof ADMIN_ACTIONS];

/** Путь в раздел с действием: `/admin/modules?action=register-service`. */
export const buildAdminActionPath = (
  route: string,
  action: TAdminAction
): string =>
  `${getFullAdminRoute(route)}?${ADMIN_ACTION_PARAM}=${encodeURIComponent(action)}`;

/** Вкладка раздела «Документы» в адресе: `?tab=templates` переживает перезагрузку и «назад». */
export const ADMIN_DOCS_TAB_PARAM = 'tab';

export const ADMIN_DOCS_TABS = {
  documents: 'documents',
  templates: 'templates'
} as const;

export type TAdminDocsTab =
  (typeof ADMIN_DOCS_TABS)[keyof typeof ADMIN_DOCS_TABS];

/** Раздел «Документы» на вкладке: документы — без параметра, шаблоны — `?tab=templates`. */
export const buildAdminDocsTabPath = (tab: TAdminDocsTab): string =>
  tab === ADMIN_DOCS_TABS.documents
    ? getFullAdminRoute(ADMIN_ROUTES.docs)
    : `${getFullAdminRoute(ADMIN_ROUTES.docs)}?${ADMIN_DOCS_TAB_PARAM}=${tab}`;

/**
 * Открытая карточка записи журнала в адресе: `/admin/audit?entry=<id>` — ссылкой на запись
 * можно поделиться, после перезагрузки карточка откроется снова.
 */
export const ADMIN_AUDIT_ENTRY_PARAM = 'entry';
